/**
 * 配置管理Store (Pinia)
 * 应用配置状态管理
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import WebStore from './web-store';

// 使用Web版本的Store
const store = new WebStore();

// ============= 类型定义 =============

/**
 * 表单预设配置
 */
export interface FormPreset {
  languageSelect: string;
  voiceSelect: string;
  voiceStyleSelect: string;
  role: string;
  speed: number;
  pitch: number;
  api: number;
}

/**
 * 表单配置映射（预设名称 -> 配置）
 */
export type FormConfigMap = Record<string, FormPreset>;

/**
 * 表单配置列表项
 */
export interface FormConfigListItem {
  tagName: string;
  content: FormPreset;
}

export const useConfigStore = defineStore('config', () => {
  // ============= 状态定义 =============

  const config = ref({
    language: store.get("language") || "zh",
    formConfigJson: store.get("FormConfig") || {},
    formConfigList: [] as FormConfigListItem[], // 计算属性，从 formConfigJson 生成
    audition: store.get("audition") || "如果你觉得这个项目还不错， 欢迎Star、Fork和PR。你的Star是对作者最好的鼓励。",
    autoplay: store.get("autoplay") !== undefined ? store.get("autoplay") : true,
    updateNotification: store.get("updateNotification") !== undefined ? store.get("updateNotification") : true,
    titleStyle: store.get("titleStyle") || "custom",
    api: store.get("api") || 5,  // 默认值为5，免费TTS服务
    formatType: store.get("formatType") || "mp3",
    speechKey: store.get("speechKey") || "",
    serviceRegion: store.get("serviceRegion") || "",
    thirdPartyApi: store.get("thirdPartyApi") || "https://tts88.top/cognitiveservices/v1",
    disclaimers: store.get("disclaimers") || false,
    retryCount: store.get("retryCount") || 3,
    retryInterval: store.get("retryInterval") || 1000,
    openAIKey: store.get("openAIKey") || "",
    openAIBaseUrl: store.get("openAIBaseUrl") || "https://api.openai.com/v1", // ✅ 新增
    openAIModel: store.get("openAIModel") || "tts-1",
    openAIVoice: store.get("openAIVoice") || "alloy",
    gptModel: store.get("gptModel") || "gpt-3.5-turbo", // ✅ 新增
    tts88Key: store.get("tts88Key") || "", // ✅ 新增
    theme: store.get("theme") || "auto",
    fontSize: store.get("fontSize") || "medium",
    showAdvancedOptions: store.get("showAdvancedOptions") || false,
    hasShownFirstTimeHint: store.get("hasShownFirstTimeHint") || false, // ✅ 新增
    showComparisonTable: store.get("showComparisonTable") || false // ✅ 新增
  });

  // ✅ 新增：FormConfig 状态声明
  const FormConfig = ref<FormConfigMap>(store.get("FormConfig") || {});

  // ============= 计算属性 =============
  
  // 强制浅色模式，禁用暗色主题
  const isDarkMode = computed(() => false);
  
  const isAutoplayEnabled = computed(() => config.value.autoplay);
  
  const currentLanguage = computed(() => config.value.language);
  
  const apiType = computed(() => config.value.api);
  
  const audioFormat = computed(() => config.value.formatType);

  // ============= Actions =============
  
  /**
   * 更新配置项
   */
  const updateConfig = (key: string, value: any) => {
    (config.value as any)[key] = value;
    store.set(key, value);
  };
  
  /**
   * 批量更新配置
   */
  const updateConfigs = (updates: Record<string, any>) => {
    Object.entries(updates).forEach(([key, value]) => {
      (config.value as any)[key] = value;
      store.set(key, value);
    });
  };
  
  /**
   * 获取配置项
   */
  const getConfig = (key: string) => {
    return (config.value as any)[key];
  };
  
  /**
   * 重置配置到默认值
   */
  const resetConfig = () => {
    const defaultConfig = {
      language: "zh",
      formConfigJson: {},
      formConfigList: [] as FormConfigListItem[],
      audition: "如果你觉得这个项目还不错， 欢迎Star、Fork和PR。你的Star是对作者最好的鼓励。",
      autoplay: true,
      updateNotification: true,
      titleStyle: "custom",
      api: 5,
      formatType: "mp3",
      speechKey: "",
      serviceRegion: "",
      thirdPartyApi: "",
      disclaimers: false,
      retryCount: 3,
      retryInterval: 1000,
      openAIKey: "",
      openAIBaseUrl: "https://api.openai.com/v1", // ✅ 新增
      openAIModel: "tts-1",
      openAIVoice: "alloy",
      gptModel: "gpt-3.5-turbo", // ✅ 新增
      tts88Key: "", // ✅ 新增
      theme: "auto",
      fontSize: "medium",
      showAdvancedOptions: false,
      hasShownFirstTimeHint: false, // ✅ 新增
      showComparisonTable: false // ✅ 新增
    };
    
    config.value = defaultConfig;
    
    // 清除本地存储
    Object.keys(defaultConfig).forEach(key => {
      store.set(key, (defaultConfig as any)[key]);
    });
  };
  
  /**
   * 导出配置
   */
  const exportConfig = () => {
    return JSON.stringify(config.value, null, 2);
  };
  
  /**
   * 导入配置
   */
  const importConfig = (configJson: string) => {
    try {
      const importedConfig = JSON.parse(configJson);
      
      // 验证配置格式
      if (typeof importedConfig !== 'object' || importedConfig === null) {
        throw new Error('配置格式无效');
      }
      
      // 更新配置
      updateConfigs(importedConfig);
      
      return true;
    } catch (error) {
      console.error('导入配置失败:', error);
      return false;
    }
  };
  
  /**
   * 设置主题 (已禁用，强制浅色模式)
   */
  const setTheme = (_theme: 'light' | 'dark' | 'auto') => {
    // 强制使用浅色主题，忽略传入参数
    updateConfig('theme', 'light');
    document.documentElement.classList.remove('dark');
  };
  
  /**
   * 设置语言
   */
  const setLanguage = (language: string) => {
    updateConfig('language', language);
    
    // 这里可以触发i18n语言切换
    // i18n.global.locale = language;
  };
  
  /**
   * 设置字体大小
   */
  const setFontSize = (size: 'small' | 'medium' | 'large') => {
    updateConfig('fontSize', size);
    
    // 应用字体大小到DOM
    document.documentElement.classList.remove('font-small', 'font-medium', 'font-large');
    document.documentElement.classList.add(`font-${size}`);
  };
  
  /**
   * 切换自动播放
   */
  const toggleAutoplay = () => {
    updateConfig('autoplay', !config.value.autoplay);
  };
  
  /**
   * 切换高级选项显示
   */
  const toggleAdvancedOptions = () => {
    updateConfig('showAdvancedOptions', !config.value.showAdvancedOptions);
  };

  // ============= FormConfig 相关方法 =============

  /**
   * 设置表单配置
   */
  const setFormConfig = (formConfig: FormConfigMap) => {
    FormConfig.value = formConfig;
    store.set("FormConfig", formConfig);
  };

  /**
   * 获取表单配置
   */
  const getFormConfig = (): FormConfigMap => {
    return FormConfig.value;
  };

  /**
   * 更新特定预设的配置
   */
  const updateFormPreset = (presetName: string, preset: FormPreset) => {
    FormConfig.value[presetName] = preset;
    store.set("FormConfig", FormConfig.value);
  };

  /**
   * 删除预设配置
   */
  const deleteFormPreset = (presetName: string) => {
    delete FormConfig.value[presetName];
    store.set("FormConfig", FormConfig.value);
  };

  // ============= Settings.vue 中使用的方法 =============

  /**
   * 生成表单配置列表（从 formConfigJson 转换为数组格式）
   */
  const genFormConfig = () => {
    const formConfigJson = config.value.formConfigJson || {};
    config.value.formConfigList = Object.entries(formConfigJson).map(([tagName, content]) => ({
      tagName,
      content: content as FormPreset
    }));
  };

  /**
   * 设置试听配置
   */
  const setAuditionConfig = () => {
    store.set("audition", config.value.audition);
  };

  /**
   * 设置自动播放
   */
  const setAutoPlay = () => {
    store.set("autoplay", config.value.autoplay);
  };

  /**
   * 更新通知变化
   */
  const updateNotificationChange = () => {
    store.set("updateNotification", config.value.updateNotification);
  };

  /**
   * 设置语音密钥
   */
  const setSpeechKey = () => {
    store.set("speechKey", config.value.speechKey);
  };

  /**
   * 设置服务区域
   */
  const setServiceRegion = () => {
    store.set("serviceRegion", config.value.serviceRegion);
  };

  /**
   * 设置OpenAI密钥
   */
  const setOpenAIKey = () => {
    store.set("openAIKey", config.value.openAIKey);
  };

  /**
   * 设置OpenAI Base URL
   */
  const setOpenAIBaseUrl = () => {
    store.set("openAIBaseUrl", config.value.openAIBaseUrl);
  };

  /**
   * 设置GPT模型
   */
  const setGPTModel = () => {
    store.set("gptModel", config.value.gptModel);
  };

  /**
   * 设置重试次数
   */
  const setRetryCount = () => {
    store.set("retryCount", config.value.retryCount);
  };

  /**
   * 设置重试间隔
   */
  const setRetryInterval = () => {
    store.set("retryInterval", config.value.retryInterval);
  };

  /**
   * 设置第三方API
   */
  const setThirdPartyApi = () => {
    store.set("thirdPartyApi", config.value.thirdPartyApi);
  };

  /**
   * 设置TTS88密钥
   */
  const setTTS88Key = () => {
    store.set("tts88Key", config.value.tts88Key);
  };

  /**
   * 重新加载配置（从 localStorage 读取）
   */
  const reloadConfig = () => {
    // 从 localStorage 重新读取所有配置
    config.value = {
      language: store.get("language") || "zh",
      formConfigJson: store.get("FormConfig") || {},
      formConfigList: [] as FormConfigListItem[],
      audition: store.get("audition") || "如果你觉得这个项目还不错， 欢迎Star、Fork和PR。你的Star是对作者最好的鼓励。",
      autoplay: store.get("autoplay") !== undefined ? store.get("autoplay") : true,
      updateNotification: store.get("updateNotification") !== undefined ? store.get("updateNotification") : true,
      titleStyle: store.get("titleStyle") || "custom",
      api: store.get("api") || 5,
      formatType: store.get("formatType") || "mp3",
      speechKey: store.get("speechKey") || "",
      serviceRegion: store.get("serviceRegion") || "",
      thirdPartyApi: store.get("thirdPartyApi") || "https://tts88.top/cognitiveservices/v1",
      disclaimers: store.get("disclaimers") || false,
      retryCount: store.get("retryCount") || 3,
      retryInterval: store.get("retryInterval") || 1000,
      openAIKey: store.get("openAIKey") || "",
      openAIBaseUrl: store.get("openAIBaseUrl") || "https://api.openai.com/v1",
      openAIModel: store.get("openAIModel") || "tts-1",
      openAIVoice: store.get("openAIVoice") || "alloy",
      gptModel: store.get("gptModel") || "gpt-3.5-turbo",
      tts88Key: store.get("tts88Key") || "",
      theme: store.get("theme") || "auto",
      fontSize: store.get("fontSize") || "medium",
      showAdvancedOptions: store.get("showAdvancedOptions") || false,
      hasShownFirstTimeHint: store.get("hasShownFirstTimeHint") || false,
      showComparisonTable: store.get("showComparisonTable") || false
    };

    // 重新加载 FormConfig
    FormConfig.value = store.get("FormConfig") || {};

    // 重新生成表单配置列表
    genFormConfig();

    // 重新应用主题和字体设置
    setTheme(config.value.theme);
    setFontSize(config.value.fontSize);
  };

  // ============= 初始化 =============

  // 强制应用浅色主题
  document.documentElement.classList.remove('dark');
  setTheme('light');

  // 应用初始字体大小
  setFontSize(config.value.fontSize);

  return {
    // 状态
    config,
    FormConfig, // ✅ 导出 FormConfig 状态

    // 计算属性
    isDarkMode,
    isAutoplayEnabled,
    currentLanguage,
    apiType,
    audioFormat,

    // 方法
    updateConfig,
    updateConfigs,
    getConfig,
    resetConfig,
    exportConfig,
    importConfig,
    setTheme,
    setLanguage,
    setFontSize,
    toggleAutoplay,
    toggleAdvancedOptions,

    // FormConfig 相关方法
    setFormConfig,
    getFormConfig,
    updateFormPreset,
    deleteFormPreset,

    // ✅ Settings.vue 中使用的方法
    genFormConfig,
    setAuditionConfig,
    setAutoPlay,
    updateNotificationChange,
    setSpeechKey,
    setServiceRegion,
    setOpenAIKey,
    setOpenAIBaseUrl,
    setGPTModel,
    setRetryCount,
    setRetryInterval,
    setThirdPartyApi,
    setTTS88Key,
    reloadConfig
  };
});
